import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Coins, ArrowRight, Shield } from 'lucide-react';

interface AmountStepProps {
  amount: string;
  onAmountChange: (amount: string) => void;
  onContinue: () => void;
  minDeposit: number;
  maxDeposit: number;
}

const presetAmounts = [5, 10, 25, 50, 100];

export function AmountStep({ 
  amount, 
  onAmountChange, 
  onContinue, 
  minDeposit, 
  maxDeposit 
}: AmountStepProps) {
  const amountNum = parseFloat(amount) || 0;
  const creditsToReceive = Math.floor(amountNum * 100);
  const isValidAmount = amountNum >= minDeposit && amountNum <= maxDeposit;

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Label className="text-sm font-medium">Select Amount (USD)</Label>
        <div className="flex flex-wrap gap-2">
          {presetAmounts.map((preset) => (
            <Button
              key={preset}
              variant={amountNum === preset ? 'default' : 'outline'}
              size="sm"
              onClick={() => onAmountChange(preset.toString())}
              className="min-w-[60px]"
            >
              ${preset}
            </Button>
          ))}
        </div>
        <Input
          type="number"
          placeholder="Or enter custom amount"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          min={minDeposit}
          max={maxDeposit}
          step="0.01"
          className="mt-2"
        />
        <p className="text-xs text-muted-foreground">
          Min: ${minDeposit} • Max: ${maxDeposit}
        </p>
      </div>

      {isValidAmount && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">You'll receive:</span>
            <span className="flex items-center gap-1.5 font-semibold text-primary text-lg">
              <Coins className="h-5 w-5" />
              {creditsToReceive.toLocaleString()} credits
            </span>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
        <Shield className="h-4 w-4 text-primary shrink-0" />
        <span>Verified via TronGrid blockchain monitoring</span>
      </div>

      <Button
        className="w-full"
        size="lg"
        onClick={onContinue}
        disabled={!isValidAmount}
      >
        Continue to Payment
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}
